import { Box, Chip, Divider, Tooltip, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import { getConfirmedOrders } from 'redux/reducers/orders/selectors';
import { Duties } from 'components/Order/OrderDetails/OrderDetailsRow/OrderDetailsRowChild/Duties';
import { Total } from 'components/Order/OrderDetails/OrderDetailsRow/OrderDetailsRowChild/Total';
import { representationSchemeType } from 'services/constants/representationSchemeType';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { TOrderReturnsItemProps } from './types';
import { COLORS } from 'services/constants/colors';
import { EasChip } from 'ui/EasChip';
import { useNavigate } from 'react-router-dom';
import { EasTooltip } from 'ui/EasTooltip';

export const OrderReturnsItem: FC<TOrderReturnsItemProps> = ({
	orderReturnsItem,
	detalization,
	returnedOrderBreakdown,
}) => {
	const navigate = useNavigate();
	const orders = useSelector(getConfirmedOrders);
	const paymentCurrency = orders?.data?.[0]?.paymentCurrency;

	return (
		<Box>
			<Box
				sx={{
					border: '1px solid #00000020',
					borderRadius: '12px',
					padding: '10px',
					marginBottom: '10px',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'start',
						alignItems: 'center',
						gap: 1,
						mb: 2,
					}}
				>
					<Typography variant="body2" color="text.secondary">
						{orderReturnsItem?.timestamp &&
							DateTime.fromISO(orderReturnsItem?.timestamp).toFormat('yyyy-MM-dd, HH:mm')}
					</Typography>
					<EasChip label={orderReturnsItem?.status?.toLowerCase()?.replaceAll('_', ' ')} />
				</Box>

				{returnedOrderBreakdown?.map((child, index) => (
					<Box sx={{ ml: 2 }} key={`${child.idProvidedByEm}-${index}`}>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								justifyContent: 'space-between',
								gap: 2,
							}}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									gap: 1,
								}}
							>
								{' '}
								<Typography
									variant="body2"
									sx={{
										fontWeight: 'bold',
									}}
									color="primary"
								>
									{representationSchemeType?.[
										child.repScheme as keyof typeof representationSchemeType
									] ?? child.repScheme}
								</Typography>
								<Typography variant="body2">
									<span style={{ fontWeight: 'bold' }}>{child.idProvidedByEm} — </span>
									{child.shortDescription}
								</Typography>
								<EasTooltip title="View on refund details page">
									<EasChip
										label={`${String(child.returnItemQuantity)} returned`}
										bgcolor={COLORS.RETURNED}
										onClick={() => {
											navigate(`/refund/${orderReturnsItem.orderId}/${orderReturnsItem.id}`);
										}}
									/>
								</EasTooltip>
							</Box>

							{detalization ? (
								<Box
									sx={{
										display: 'flex',
										gap: 1,
									}}
								>
									<Typography
										variant="body2"
										sx={{
											fontSize: '.8rem',
										}}
										color="text.secondary"
									>
										<AmountAndCurrency
											amount={child.returnItemAmount / child.returnItemQuantity}
											currency={paymentCurrency ?? 'EUR'}
											bold
											size={0.8}
											color="text.secondary"
										/>
										&nbsp;&times;&nbsp;{child.returnItemQuantity}&nbsp;=&nbsp;
									</Typography>
									<AmountAndCurrency
										amount={child.returnItemAmount}
										currency={paymentCurrency ?? 'EUR'}
										bold
										size={0.9}
										color="text.secondary"
									/>
								</Box>
							) : (
								<AmountAndCurrency
									amount={
										+(child.returnItemAmount ?? 0) +
										+(child.returnVatValue ?? 0) +
										+(child.returnEasFee ?? 0) +
										+(child.returnVatOnEasFee ?? 0) +
										+(child.returnDeliveryCost ?? 0) +
										+(child.returnVatOnDeliveryCharge ?? 0)
									}
									currency={paymentCurrency ?? 'EUR'}
									bold
									size={1}
									color={COLORS.PRIMARY}
								/>
							)}
						</Box>
						{detalization && (
							<>
								<Duties
									duty={child.returnVatValue}
									nameOfDuty="Item VAT"
									paymentCurrency={paymentCurrency ?? ''}
								/>
								{!!child.returnDeliveryCost && (
									<Duties
										duty={child.returnDeliveryCost}
										nameOfDuty="Delivery Charge"
										paymentCurrency={paymentCurrency ?? ''}
									/>
								)}
								{!!child.returnVatOnDeliveryCharge && (
									<Duties
										duty={child.returnVatOnDeliveryCharge}
										nameOfDuty="VAT on Delivery Charge"
										paymentCurrency={paymentCurrency ?? ''}
									/>
								)}
								{!!child.returnEasFee && (
									<Duties
										duty={child.returnEasFee}
										nameOfDuty="EAS Fee"
										paymentCurrency={paymentCurrency ?? ''}
									/>
								)}
								{!!child.returnVatOnEasFee && (
									<Duties
										duty={child.returnVatOnEasFee}
										nameOfDuty="VAT on EAS Fee (22%)"
										paymentCurrency={paymentCurrency ?? ''}
									/>
								)}

								<Divider sx={{ mt: 0.4, mb: 0.4, ml: 4 }} />
								<Total
									totalPerItem={child?.returnItemAmount ?? 0}
									quantity={child.returnItemQuantity}
									totalCost={
										+(child.returnItemAmount ?? 0) +
										+(child.returnVatValue ?? 0) +
										+(child.returnEasFee ?? 0) +
										+(child.returnVatOnEasFee ?? 0) +
										+(child.returnDeliveryCost ?? 0) +
										+(child.returnVatOnDeliveryCharge ?? 0)
									}
									paymentCurrency={paymentCurrency ?? ''}
								/>
							</>
						)}
					</Box>
				))}
				{(returnedOrderBreakdown?.length ?? 0) > 1 && (
					<>
						<Divider sx={{ mt: 1, mb: 1, ml: 4 }} />

						<Box sx={{ display: 'flex', justifyContent: 'end' }}>
							<AmountAndCurrency
								amount={orderReturnsItem?.totalReturnAmount}
								currency={paymentCurrency ?? 'EUR'}
								bold
								size={1}
								color={COLORS.PRIMARY}
							/>
						</Box>
					</>
				)}
			</Box>
		</Box>
	);
};
